import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { useLang } from "@/i18n/LangContext";
import { useLocalizedNav } from "@/i18n/LocalizedLink";

/**
 * Landing page after Google → backend redirect.
 *
 * URL: /{lang}/auth/google?token=<jwt>&next=<path>&uid=&username=&email=
 * or   /{lang}/auth/google?error=<message>
 *
 * The heavy lifting (code exchange, user creation/linking) happens on the
 * backend at /api/auth/google/callback. This page only picks up the JWT.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
 */
export default function GoogleCallback() {
  const loc = useLocation();
  const lnav = useLocalizedNav();
  const { setSessionFromServer } = useUserAuth();
  const { t } = useLang();
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const p = new URLSearchParams(loc.search);
    const err = p.get("error");
    const token = p.get("token");
    if (err) {
      setError(err);
      return;
    }
    if (!token) {
      setError("Missing token");
      return;
    }
    const user = {
      id: p.get("uid") || "",
      username: p.get("username") || "",
      email: p.get("email") || "",
    };
    setSessionFromServer(token, user);
    toast.success(t("auth.google_welcome"));
    const next = p.get("next");
    const dest = next && next.startsWith("/") ? next : "/";
    lnav(dest, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main className="min-h-[60vh] grid place-items-center p-6" data-testid="google-callback">
      <div className="max-w-md w-full text-center">
        {!error ? (
          <div className="text-zinc-300">
            <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-brand-cyan" />
            <p className="text-lg font-semibold mb-1">{t("auth.google_finishing")}</p>
            <p className="text-sm text-zinc-500">{t("common.loading")}</p>
          </div>
        ) : (
          <div className="bg-red-500/5 border border-red-500/30 rounded-xl p-6" data-testid="google-callback-error">
            <AlertCircle className="w-6 h-6 mx-auto mb-3 text-red-400" />
            <p className="text-red-300 font-semibold mb-1">{t("auth.google_failed")}</p>
            <p className="text-sm text-red-400/80 break-all">{error}</p>
            <button onClick={() => lnav("/signin", { replace: true })} className="mt-5 px-5 py-2 rounded-lg bg-white/5 border border-white/10 text-sm hover:bg-white/10">
              {t("nav.sign_in")}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
